import { BiomarkerPanelType } from '@oncolens/oncolens-ui/lib/components/BiomarkerResults/BiomarkerResults.api';
import * as yup from 'yup';

const attachmentSchema = yup.object().shape({
  id: yup.string(),
  fileName: yup.string(),
  reportDate: yup.date().nullable(),
  comments: yup.string().nullable(),
  sortOrder: yup.number().nullable(),
});

const riskFactorSchema = yup.object().shape({
  description: yup.string().required('Description is required.'),
  value: yup.string().nullable(),
  comments: yup.string().nullable(),
});

const treatmentHistorySchema = yup.object().shape({
  treatmentType: yup.string().required('Treatment Type is required.'),
  treatmentDescription: yup.string().nullable(),
  startDate: yup.date().nullable(),
  endDate: yup.date().nullable(),
  status: yup.string().required("Status is required."),
  comments: yup.string().nullable(),
  sortOrder: yup.number().nullable()
});

const eventHistorySchema = yup.object().shape({
  status: yup.string().required(),
  eventName: yup.string().nullable(),
  eventDate: yup.date().nullable(),
  comments: yup.string().nullable(),
  sortOrder: yup.number().nullable()
});

const biomarkerSchema = yup.object().shape({
  biomarkerApiId: yup.string().nullable(),
  biomarkerName: yup.string().required('Biomarker Name is required.'),
  value: yup.string().nullable(),
  interpretation: yup.string().nullable(),
  comments: yup.string().nullable(),
  testMethodology: yup.string().nullable(),
  alleleFrequency: yup.string().nullable(),
});

const biomarkerPanelSchema = yup.object().shape({
  collectionDate: yup.date().nullable(),
  collectionSite: yup.string().nullable(),
  reportingLab: yup.string().nullable(),
  cancerLocationId: yup.string().required('Disease location is required.'),
  cancerPathologyId: yup.string().required('Disease type is required.'),
  cancerPathologyOtherComments: yup.string().nullable(),
  cancerTypeId: yup.string().required('Cancer type is required.'),
  panelName: yup.string().required('Panel Name is required.'),
  completedDate: yup.date().nullable(),
  panelType: yup.string().nullable(),
  biomarkers: yup.array().of(biomarkerSchema),
});

const biomarkerDropdownItemSchema = yup.object().shape({
  id: yup.string(),
  panel: biomarkerPanelSchema.when('type', {
    is: BiomarkerPanelType.Panel,
    otherwise: yup.object().nullable()
  }),
});

const inputRequestSchema = yup.object().shape({
  'id': yup.string(),
  'requestText': yup.string().nullable(),
  'inputRequested': yup.boolean().nullable()
});

const simpleStagingSchema = yup.object().shape({
  'stagingGradingSystem': yup.string().required('Staging system is required.'),
  'stagingValue': yup.string().required('Value is required.'),
  'otherStagingDetails': yup.string().nullable()
})

const episodeSchema = yup.object().shape({
  'isTargetEpisode': yup.boolean().required().meta({ section: 'episodes' }),
  'areasOfMetastasisOtherComments': yup.string().nullable().meta({ section: 'episodes' }),
  'areasOfRecurrenceOtherComments': yup.string().nullable().meta({ section: 'episodes' }),
  'diagnosisStatus': yup.string().required("Diagnosis Status is required.").meta({ section: 'episodes' }),
  'episodeDate': yup.date().required('Date is required.').nullable().meta({ section: 'episodes' }),
  'cancerBehaviorId': yup.string().nullable()
    .when("isTargetEpisode", {
      is: true,
      then: yup.string().required("Behavior is required.")
    }).meta({ section: 'episodes' }),
  'cancerSettingId': yup.string().nullable()
    .when("isTargetEpisode", {
      is: true,
      then: yup.string().required("Setting is required.")
    }).meta({ section: 'episodes' }),
  'laterality': yup.string().nullable().meta({ section: 'episodes' }),
  'recurrenceExtentId': yup.string().nullable().meta({ section: 'episodes' }),
  'areasOfRecurrence': yup.array().of(yup.string()).nullable().meta({ section: 'episodes' }),
  'areasOfMetastasis': yup.array().of(yup.string()).nullable().meta({ section: 'episodes' }),
  'isSaved': yup.boolean().required()
});

const diseaseSchema = yup.object().shape({
  id: yup.string().nullable(),
  cancerLocationId: yup
    .string()
    .required('Cancer Location is required.')
    .meta({ section: 'diseases' }),
  cancerPathologyId: yup
    .string()
    .required('Disease Type is required.')
    .meta({ section: 'diseases' }),
  cancerPathologyOtherComments: yup
    .string()
    .nullable()
    .meta({ section: 'diseases' }),
  initialDiagnosisDate: yup.date().nullable().meta({ section: 'diseases' }),
  episodes: yup.lazy((map: any) => {
    const newSchema = yup.object(mapRules(map, episodeSchema));

    return newSchema;
  }),
});

const mapRules = (map: any, rule: any) => {
  if(!map) return {};
  const keys = Object.keys(map);
  const reduction = keys
    .reduce((newMap, key) => ({...newMap, [key]: rule}), {});
  return reduction;
}

const schema = yup.object().shape({
  submissionHospital: yup.object().shape({
    name: yup.string(),
    uuid: yup.string(),
    usesV2: yup.boolean()
  }),
  disallowPhi: yup.boolean(),
  patient: yup.object()
    .when('disallowPhi', {
      is: true,
      then: yup.object().shape({
        age: yup.number().typeError('Age is required').required('Age is required'),
        sexId: yup.string().required('Sex is required'),
        raceId: yup.string().nullable(),
        ethnicityId: yup.string().nullable(),
        referenceId: yup.string().nullable(),
      }),
      otherwise: yup.object().shape({
        firstName: yup.string().required('First name is required'),
        lastName: yup.string().required('Last name is required'),
        sexId: yup.string().required('Sex is required'),
        dateOfBirth: yup.date().required('Date of birth is required'),
        raceId: yup.string().nullable(),
        ethnicityId: yup.string().nullable(),
        mrn: yup.string().trim().when(['isMrnRequired'], {
          is: (isMrnRequired: boolean) => isMrnRequired,
          then: yup.string().required('MRN is required')
        }),
        isMrnRequired: yup.bool().nullable(),
        referenceId: yup.string().nullable(),
      }),
  }),
  event: yup.object().shape({
    legacyTumorBoardId: yup.string().required('Conference is required'),
    suggestedDate: yup.string().nullable(),
    topicForDiscussion: yup.string().nullable(),
    isPending: yup.boolean().default(true),
    meetingId: yup.string().nullable(),
    presentingProvider: yup.string().required('Presenting provider is required.'),
    facilities: yup.array().of(
      yup.string()
    ).min(1).required('Please select at least 1 facility for accreditation.'),
    isHighPriority: yup.boolean().default(false),
  }),
  diseases: yup.lazy((map: any) => {
    const newSchema = yup.object(
      mapRules(map, diseaseSchema)
    );

    return newSchema;
  }),
  patientHistory: yup.object().shape({
    chiefComplaint: yup.string().nullable(),
    medicalHistory: yup.string().nullable(),
    familyHistory: yup.string().nullable(),
    socialHistory: yup.string().nullable(),
    menopausalStatus: yup.string().nullable(),
    breastOvarianCancerNear: yup.number().nullable(),
    miscellaneousCaseDetails: yup.string().nullable()
  }).nullable(),
  inputRequests: yup.object().shape({
    'specimenNumber': yup.string().nullable(),
    'pathology': inputRequestSchema,
    'radiology': inputRequestSchema,
    'admin': inputRequestSchema,
    'genetics': inputRequestSchema,
  }),
  staging: yup.lazy((_value: string) => {
    return yup.object().shape({
      id: yup.string().nullable(),
      m: yup.string().nullable(),
      n: yup.string().nullable(),
      t: yup.string().nullable(),
      stageGroup: yup.string().nullable(),
      grade: yup.string().nullable(),
      timing: yup.string().nullable(),
      erInterpretation: yup.string().nullable(),
      prInterpretation: yup.string().nullable(),
      her2: yup.string().nullable(),
    }).concat(yup.object().shape({
      id: yup.string().nullable(),
      blclStage: yup.string().nullable(),
      tumorSite: yup.string().nullable(),
      stagingDetails: yup.string().when(
        ['isStagingDetailsRequired'],
        {
          is: (isStagingDetailsRequired: boolean) => isStagingDetailsRequired,
          then: yup.string().required('Staging details are required'),
          otherwise: yup.string().nullable()
        }
      ),
      isStagingDetailsRequired: yup.bool().nullable()
    }));
  }),
  panels: yup.array().of(biomarkerDropdownItemSchema),
  riskFactors: yup.array().of(riskFactorSchema),
  treatmentHistory: yup.array().of(treatmentHistorySchema),
  eventHistory: yup.array().of(eventHistorySchema),
  careTeam: yup.object().shape({}),
  attachments: yup.array().of(attachmentSchema),
});

export {
  schema as default,
  biomarkerPanelSchema,
  biomarkerSchema,
  riskFactorSchema,
  treatmentHistorySchema,
  eventHistorySchema,
  attachmentSchema,
  episodeSchema,
  simpleStagingSchema
};

